import { useState } from "react";
import { Link } from "react-router-dom";
import { History } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { PageLoader } from "@/components/ui/Spinner";
import { useEngAronMeta } from "../api/engAronApi";
import { useFindingsHistory } from "../api/reviewsApi";
import {
  CATEGORY_LABEL,
  FINDING_STATUS_LABEL,
  SEVERITY_COLOR,
  SEVERITY_LABEL,
} from "../utils";

/** Przekrojowa historia uwag ze WSZYSTKICH recenzji — kopalnia wiedzy o powtarzalnych
 * błędach (stąd też awansuje się uwagi do katalogu błędów). */
export function EngAronFindingsPage() {
  const meta = useEngAronMeta();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [status, setStatus] = useState("");
  const findings = useFindingsHistory({
    ...(q ? { q } : {}),
    ...(category ? { category } : {}),
    ...(severity ? { severity } : {}),
    ...(status ? { status } : {}),
  });

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Historia uwag</h1>
        <p className="text-sm text-[var(--color-text-muted)]">
          Wszystkie uwagi ze wszystkich recenzji — szukaj powtarzalnych błędów i awansuj je
          do katalogu błędów (z poziomu recenzji).
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Input placeholder="Szukaj w treści…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[{ value: "", label: "Kategoria: wszystkie" }, ...(meta.data?.categories ?? [])]}
        />
        <Select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          options={[
            { value: "", label: "Waga: wszystkie" },
            ...Object.entries(SEVERITY_LABEL).map(([value, label]) => ({ value, label })),
          ]}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: "", label: "Status: wszystkie" },
            ...Object.entries(FINDING_STATUS_LABEL).map(([value, label]) => ({ value, label })),
          ]}
        />
      </div>

      {findings.isLoading ? (
        <PageLoader />
      ) : (findings.data ?? []).length === 0 ? (
        <div className="border border-dashed border-[var(--color-border-strong)] p-10 text-center space-y-2">
          <History size={28} className="mx-auto text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">Brak uwag.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(findings.data ?? []).map((f) => (
            <Link
              key={f.id}
              to={`/eng-aron/reviews/${f.review}`}
              className="block border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 hover:border-[var(--color-primary)] transition-colors"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge color={SEVERITY_COLOR[f.severity]}>{SEVERITY_LABEL[f.severity]}</Badge>
                <Badge color="gray">{CATEGORY_LABEL[f.category] ?? f.category}</Badge>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {f.review_label} · uwaga {f.number}
                  {f.page_refs.length ? ` · s. ${f.page_refs.join(", ")}` : ""}
                </span>
                {f.promoted_entry && <span className="text-xs text-green-600">✓ w bazie</span>}
              </div>
              <p className="text-sm text-[var(--color-text-primary)] mt-1 line-clamp-2 whitespace-pre-wrap">
                {f.body}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
